// ─────────────────────────────────────────────────────────────────
// Restricted Area Page
// Matches Xamarin RestrictedAreaPage + RestrictedAreaPageViewModel
// Guard page that blocks access when environment validations fail.
// ─────────────────────────────────────────────────────────────────

import { WifiOff, ServerCrash, Wrench, Settings } from 'lucide-react';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ROUTES } from '@/constants';
import {
  validateEnvironment,
  checkAuthSession,
  getCurrentPosition,
} from '@/service/LocationValidationService';
import { resolveRestrictedError } from '@/service/types/restrictedAreaTypes';
import { useAppSelector, selectSettings, selectUser } from '@/store';
import { cn } from '@/utils/cn';

import { Spinner } from './LoadingPage';

import type { RestrictedAreaError, RestrictedErrorType } from '@/service/types/restrictedAreaTypes';
import type { AppSettings } from '@/types';
import type { LucideIcon } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────
// Inline: Icon Display
// ─────────────────────────────────────────────────────────────────

const iconMap: Record<RestrictedErrorType, LucideIcon | null> = {
  restricted_location: null,
  plant_disabled: null,
  no_internet: WifiOff,
  server_error: ServerCrash,
  maintenance: Wrench,
  gps_off: null,
  config_missing: Settings,
};

function RestrictedIconDisplay({ errorType }: { errorType: RestrictedErrorType }) {
  const Icon = iconMap[errorType];

  return (
    <div className="flex items-center justify-center">
      {Icon === null ? (
        <img src="/images/location_off.svg" alt="Location disabled" className="h-40 w-40" />
      ) : (
        <Icon className="h-32 w-32 text-white" strokeWidth={1.5} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Inline: Message Block
// ─────────────────────────────────────────────────────────────────

function RestrictedMessageBlock({ error }: { error: RestrictedAreaError }) {
  return (
    <div className="text-center px-6">
      <p className="text-lg font-bold font-poppins text-white leading-relaxed">
        {error.message}
        {error.subMessage ? ` ${error.subMessage}` : ''}
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export function RestrictedAreaPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const settings = useAppSelector(selectSettings);
  const isAuthenticatedUser = useAppSelector(selectUser);

  const errorMessage = searchParams.get('error') ?? t('restrictedArea.accessRestricted');

  const [loading, setLoading] = useState(false);
  const [currentError, setCurrentError] = useState<RestrictedAreaError>(() =>
    resolveRestrictedError(errorMessage)
  );

  // Re-resolve error if search param changes
  useEffect(() => {
    setCurrentError(resolveRestrictedError(errorMessage));
  }, [errorMessage]);

  // Debug location toggle — Matches Xamarin ConfigInfo.Settings.LocationDebugger
  const isDebugEnabled = useMemo(() => {
    if (!settings) return false;
    const s = settings as AppSettings & Record<string, string | undefined>;
    return s['LocationDebugger'] === 'true' || s['LOCATION_DEBUGGER'] === 'true';
  }, [settings]);

  // ─── Retry / Try Again ───────────────────────────────────────
  // Matches Xamarin LoginCommand: validate -> redirect based on session state
  const handleRetry = useCallback(async () => {
    setLoading(true);
    try {
      const result = await validateEnvironment();

      if (!result.valid) {
        setCurrentError(
          resolveRestrictedError(result.errorMessage ?? t('restrictedArea.validationFailed'))
        );
        return;
      }

      // Validations passed — determine redirect target
      // Matches Xamarin: user+plant -> Dashboard, user but expired -> Login, else -> StartUp
      const session = checkAuthSession();

      if (session.hasUser && session.hasToken && !session.tokenExpired && session.hasPlant) {
        navigate(ROUTES.dashboard, { replace: true });
      } else if (session.hasUser && session.tokenExpired) {
        navigate(ROUTES.login, { replace: true });
      } else if (isAuthenticatedUser) {
        navigate(ROUTES.plantSelection, { replace: true });
      } else {
        navigate(ROUTES.login, { replace: true });
      }
    } catch {
      setCurrentError(resolveRestrictedError(t('restrictedArea.unexpectedError')));
    } finally {
      setLoading(false);
    }
  }, [navigate, isAuthenticatedUser, t]);

  // ─── Track Location (debug) ──────────────────────────────────
  // Matches Xamarin TrackLocationCommand
  const handleTrackLocation = useCallback(async () => {
    try {
      const position = await getCurrentPosition();
      const { latitude, longitude } = position.coords;
      window.open(
        `https://www.google.com/maps?q=${String(latitude)},${String(longitude)}`,
        '_blank',
        'noopener,noreferrer'
      );
    } catch {
      setCurrentError(resolveRestrictedError(t('restrictedArea.gpsError')));
    }
  }, [t]);

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'linear-gradient(to bottom, #003366 0%, #0B1B25 100%)' }}
    >
      {/* Error message at top */}
      <div className="pt-16 px-6">
        <RestrictedMessageBlock error={currentError} />
      </div>

      {/* Centered icon */}
      <div className="flex-1 flex items-center justify-center">
        <RestrictedIconDisplay errorType={currentError.type} />
      </div>

      {/* Bottom button area */}
      <div className="pb-12 flex flex-col items-center space-y-3">
        <button
          className={cn(
            'w-64 py-3.5 rounded-2xl border border-white/50 text-white font-poppins text-base',
            'transition-colors hover:bg-white/10 active:bg-white/20',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          onClick={() => {
            void handleRetry();
          }}
          disabled={loading}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <Spinner size="sm" />
              {t('restrictedArea.validating')}
            </span>
          ) : (
            t('restrictedArea.tryAgain')
          )}
        </button>
        {isDebugEnabled && (
          <button
            className={cn(
              'w-52 py-3 rounded-xl border border-white/30 text-white/70 font-poppins text-sm',
              'transition-colors hover:bg-white/10'
            )}
            onClick={() => {
              void handleTrackLocation();
            }}
          >
            {t('restrictedArea.trackLocation')}
          </button>
        )}
      </div>
    </div>
  );
}
